/**
 * GroundRiskAnswers Component
 *
 * Displays the five ground risk answer fields for SORA evidence review.
 * Uses the EvidenceTable component to display extracted values with citations
 * and confidence scores.
 *
 * Ground Risk Fields:
 * 1. Maximum dimensions of the unmanned aircraft
 * 2. Maximum speed in meters per second
 * 3. Whether the aircraft weighs more than 250g (yes/no)
 * 4. Whether operating in controlled ground area only (yes/no)
 * 5. Maximum population density in operational volume and ground risk buffer
 *
 * Requirements: 4.1, 4.2
 */

import React, { useMemo } from 'react';
import { EvidenceTable, EvidenceItem, EvidenceColumn } from '../EvidenceTable';
import { GroundRiskAnswer, GroundRiskField, Citation } from '../../types/extraction';
import styles from './GroundRiskAnswers.module.css';

/**
 * Human-readable labels for ground risk fields
 */
const GROUND_RISK_FIELD_LABELS: Record<GroundRiskField, string> = {
  max_dimensions: 'Maximum dimensions of the unmanned aircraft',
  max_speed_mps: 'Maximum speed (m/s)',
  weight_over_250g: 'Aircraft weighs more than 250g',
  controlled_ground_area_only: 'Operating in controlled ground area only',
  max_population_density: 'Maximum population density in operational volume and ground risk buffer',
};

/**
 * Order of fields for display
 */
const FIELD_ORDER: GroundRiskField[] = [
  'max_dimensions',
  'max_speed_mps',
  'weight_over_250g',
  'controlled_ground_area_only',
  'max_population_density',
];

/**
 * Column configuration for the ground risk answers table
 */
const GROUND_RISK_COLUMNS: EvidenceColumn[] = [
  { key: 'field', label: 'Field', width: '30%' },
  { key: 'value', label: 'Extracted Value', width: '25%' },
  { key: 'confidence', label: 'Confidence', width: '15%' },
  { key: 'citations', label: 'Citations', width: '20%' },
  { key: 'feedback', label: 'Feedback', width: '10%' },
];

/**
 * Props for the GroundRiskAnswers component
 */
export interface GroundRiskAnswersProps {
  /** Array of ground risk answers to display */
  answers: GroundRiskAnswer[];
  /** Callback when a citation is clicked */
  onCitationClick: (citation: Citation) => void;
  /** Callback when the feedback button is clicked for an answer */
  onFeedbackClick: (answerId: string) => void;
  /** Optional loading state */
  isLoading?: boolean;
  /** Optional title for the section */
  title?: string;
  /** Optional description for the section */
  description?: string;
}

/**
 * Converts a GroundRiskAnswer to an EvidenceItem for the EvidenceTable
 */
function mapAnswerToEvidenceItem(answer: GroundRiskAnswer): EvidenceItem {
  return {
    id: answer.id,
    field: GROUND_RISK_FIELD_LABELS[answer.field],
    extractedValue: answer.extractedValue,
    confidence: answer.confidence,
    citations: answer.citations,
    userCorrectedValue: answer.userCorrectedValue,
  };
}

/**
 * Sorts answers according to the defined field order
 */
function sortAnswersByFieldOrder(answers: GroundRiskAnswer[]): GroundRiskAnswer[] {
  return [...answers].sort((a, b) => {
    const indexA = FIELD_ORDER.indexOf(a.field);
    const indexB = FIELD_ORDER.indexOf(b.field);
    return indexA - indexB;
  });
}

/**
 * GroundRiskAnswers displays the five ground risk answer fields in a table format.
 * It uses the EvidenceTable component to provide consistent styling and functionality
 * including confidence badges, clickable citations, and feedback buttons.
 *
 * @example
 * <GroundRiskAnswers
 *   answers={extractionResults.groundRiskAnswers}
 *   onCitationClick={handleCitationClick}
 *   onFeedbackClick={handleFeedbackClick}
 * />
 */
export const GroundRiskAnswers: React.FC<GroundRiskAnswersProps> = ({
  answers,
  onCitationClick,
  onFeedbackClick,
  isLoading = false,
  title = 'Ground Risk Answers',
  description = 'Review the extracted ground risk information for your SORA application.',
}) => {
  // Sort answers by field order and convert to EvidenceItems
  const evidenceItems = useMemo(() => {
    const sortedAnswers = sortAnswersByFieldOrder(answers);
    return sortedAnswers.map(mapAnswerToEvidenceItem);
  }, [answers]);

  return (
    <section className={styles.container} aria-labelledby="ground-risk-answers-title">
      <header className={styles.header}>
        <h2 id="ground-risk-answers-title" className={styles.title}>
          {title}
        </h2>
        {description && (
          <p className={styles.description}>{description}</p>
        )}
      </header>

      <div className={styles.tableWrapper}>
        <EvidenceTable
          items={evidenceItems}
          columns={GROUND_RISK_COLUMNS}
          onCitationClick={onCitationClick}
          onFeedbackClick={onFeedbackClick}
          isLoading={isLoading}
          emptyMessage="No ground risk answers available. Please process your documents to extract evidence."
          ariaLabel="Ground risk answers table"
        />
      </div>

      {!isLoading && answers.length > 0 && (
        <footer className={styles.footer}>
          <p className={styles.footerText}>
            <span className={styles.fieldCount}>{answers.length}</span> of{' '}
            <span className={styles.totalFields}>{FIELD_ORDER.length}</span> ground risk fields extracted
          </p>
        </footer>
      )}
    </section>
  );
};

export default GroundRiskAnswers;
